/**
 * CNPJ formatting utilities (Brazilian company tax ID)
 */

export const cleanCnpj = (value: string): string => {
  if (!value) return "";
  return value.replace(/\D/g, "").slice(0, 14);
};

/** Format as XX.XXX.XXX/XXXX-XX */
export const formatCnpj = (value: string): string => {
  const digits = cleanCnpj(value);
  if (!digits) return "";

  if (digits.length <= 2) return digits;
  if (digits.length <= 5) {
    return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  }
  if (digits.length <= 8) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  }
  if (digits.length <= 12) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  }
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
};

export const displayCnpj = (value: string): string => {
  const digits = cleanCnpj(value);
  if (digits.length !== 14) return value;
  return formatCnpj(digits);
};

export const handleCnpjInputChange = (
  value: string,
  onChange: (formatted: string) => void
): void => {
  onChange(formatCnpj(value));
};

export const normalizeInstagramUrl = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed.replace(/\/$/, "");
  }

  if (/instagram\.com\//i.test(trimmed)) {
    return `https://${trimmed.replace(/^\/\//, "").replace(/\/$/, "")}`;
  }

  const username = trimmed.replace(/^@/, "");
  return `https://instagram.com/${username}`;
};

export const formatInstagramForDisplay = (value: string): string => {
  if (!value) return "";

  const trimmed = value.trim();
  try {
    const withProtocol = /^https?:\/\//i.test(trimmed)
      ? trimmed
      : `https://${trimmed.replace(/^\/\//, "")}`;
    const url = new URL(withProtocol);
    const host = url.hostname.replace(/^www\./i, "").toLowerCase();
    if (host === "instagram.com") {
      const username = url.pathname.replace(/^\/+|\/+$/g, "").split("/")[0];
      if (username) return `@${username}`;
    }
  } catch {
    // Fall through for plain @username / username values
  }

  if (trimmed.startsWith("@")) return trimmed;
  if (!/^https?:\/\//i.test(trimmed) && !/instagram\.com/i.test(trimmed)) {
    return `@${trimmed}`;
  }

  return trimmed;
};
